import { useSearch } from '../api/rooms'
import type { SearchHitResponse } from '../api/types'
import { errorMessage, formatBytes, formatUtc } from '../lib/format'

/**
 * Hits for the typed query, as the server filtered them: every row is a document the
 * caller may view, so Preview is always offered. The snippet is a window of the
 * extracted text around the first term.
 */
export function SearchResults({
  roomId,
  query,
  onPreview,
  onOpenFolder,
}: {
  roomId: string
  query: string
  onPreview: (documentId: string, name: string) => void
  onOpenFolder: (folderId: string | null) => void
}) {
  const search = useSearch(roomId, query)

  if (search.isPending) return <p className="muted small">Searching…</p>
  if (search.isError) return <p className="error-text small">{errorMessage(search.error)}</p>
  const { hits, mode } = search.data

  return (
    <section className="panel" aria-label="Search results">
      <p className="small muted">
        {hits.length === 0
          ? `Nothing matches “${query}”`
          : `${hits.length} document${hits.length === 1 ? '' : 's'} match “${query}”`}
        {mode === 'none' && ' (type at least two letters or digits)'}
        {mode === 'like' && hits.length > 0 && ' · plain match'}
      </p>
      {hits.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Where</th>
              <th>Match</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {hits.map((h) => (
              <tr key={h.documentId}>
                <td>
                  <div>{h.name}</div>
                  <div className="small muted">
                    v{h.versionNumber} · {formatBytes(h.sizeBytes)} · {formatUtc(h.modifiedUtc)}
                  </div>
                </td>
                <td className="small">
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => onOpenFolder(h.folderId)}
                  >
                    {h.folderId
                      ? `${h.folderIndex ?? ''} ${h.folderName ?? ''}`.trim()
                      : 'Room root'}
                  </button>
                </td>
                <td className="small">
                  <MatchLabel hit={h} />
                </td>
                <td className="td-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => onPreview(h.documentId, h.name)}
                  >
                    Preview
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

export function MatchLabel({ hit }: { hit: SearchHitResponse }) {
  return (
    <>
      {hit.matchedName && <span className="chip">name</span>}{' '}
      {hit.matchedText && <span className="chip">text</span>}
      {hit.snippet && <div className="muted search-snippet">…{hit.snippet.trim()}…</div>}
      {!hit.matchedText && hit.textStatus === 'Pending' && (
        <div className="muted">text not indexed yet</div>
      )}
      {!hit.matchedText && hit.textStatus === 'Unsupported' && (
        <div className="muted">no text layer to search</div>
      )}
    </>
  )
}
